import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Plus, ShieldCheck, Pencil, Trash2 } from 'lucide-react';
import { PageHeader, Modal, Field, Spinner, ConfirmDialog, PageLoader, fmtDateTime } from '../../components/ui';
import api, { errMsg } from '../../services/api';
import { useAdmin } from '../../context/AuthContext';

function AdminModal({ target, onClose, onSaved }) {
  const [form, setForm] = useState({ name: target?.name || '', email: target?.email || '', role: target?.role || 'ADMIN', password: '', isActive: target ? target.isActive : true });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (target && !payload.password) delete payload.password;
      if (target) await api.put(`/admin/admins/${target.id}`, payload);
      else await api.post('/admin/admins', payload);
      toast.success(target ? 'Admin updated' : 'Admin added');
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={target ? 'Edit admin' : 'Appoint an admin'} onClose={onClose} footer={<>
      <button className="btn-outline" onClick={onClose}>Cancel</button>
      <button className="btn-primary" onClick={save} disabled={busy}>{busy && <Spinner size={16} />}Save</button>
    </>}>
      <div className="space-y-4">
        <Field label="Name" required><input className="input" value={form.name} onChange={e => set('name', e.target.value)} /></Field>
        <Field label="Email" required hint="Used to log in and to receive test emails"><input type="email" className="input" value={form.email} onChange={e => set('email', e.target.value)} /></Field>
        <Field label={target ? 'New password' : 'Password'} required={!target} hint={target ? 'Leave blank to keep the current password' : 'At least 8 characters — share it with them privately'}>
          <input type="text" className="input font-mono" value={form.password} onChange={e => set('password', e.target.value)} autoComplete="new-password" />
        </Field>
        <Field label="Role" hint="Owners can also appoint and remove admins">
          <select className="input" value={form.role} onChange={e => set('role', e.target.value)}><option value="ADMIN">Admin</option><option value="OWNER">Owner</option></select>
        </Field>
        {target && <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4 accent-navy-900" checked={form.isActive} onChange={e => set('isActive', e.target.checked)} />Account active</label>}
      </div>
    </Modal>
  );
}

export default function Admins() {
  const { admin: me } = useAdmin();
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const isOwner = me?.role === 'OWNER';

  const load = () => api.get('/admin/admins').then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  if (!rows) return <PageLoader />;

  return (
    <div>
      <PageHeader title="Admins" subtitle="Appointed admins can see member contact details, NIC numbers and RCU cards, and send notifications."
        actions={isOwner && <button onClick={() => setEditing('new')} className="btn-gold"><Plus size={16} />Appoint admin</button>} />
      <div className="card divide-y">
        {rows.map(a => (
          <div key={a.id} className="flex flex-wrap items-center gap-4 p-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-navy-900 font-semibold text-gold-400">{a.name.charAt(0)}</span>
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-2 font-medium text-navy-900">
                {a.name}{a.id === me.id && <span className="text-xs text-gray-400">(you)</span>}
                {a.role === 'OWNER' && <span className="badge bg-gold-100 text-gold-800"><ShieldCheck size={12} />Owner</span>}
                {!a.isActive && <span className="badge bg-gray-200 text-gray-600">Disabled</span>}
              </p>
              <p className="text-sm text-gray-500">{a.email} · last login {fmtDateTime(a.lastLoginAt)}</p>
            </div>
            {isOwner && (
              <div className="flex gap-1">
                <button onClick={() => setEditing(a)} className="rounded p-2 text-gray-500 hover:bg-gray-100"><Pencil size={16} /></button>
                {a.id !== me.id && <button onClick={() => setDeleting(a)} className="rounded p-2 text-gray-500 hover:bg-red-50 hover:text-red-600"><Trash2 size={16} /></button>}
              </div>
            )}
          </div>
        ))}
      </div>
      {!isOwner && <p className="mt-3 text-sm text-gray-500">Only owners can appoint or remove admins.</p>}
      {editing && <AdminModal target={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {deleting && <ConfirmDialog title="Remove admin?" danger confirmLabel="Remove" message={`${deleting.name} will no longer be able to log in.`}
        onClose={() => setDeleting(null)} onConfirm={async () => { await api.delete(`/admin/admins/${deleting.id}`); toast.success('Admin removed'); load(); }} />}
    </div>
  );
}
